import React from "react";
import Helmet from "react-helmet";

function AboutHTTP() {
  return (
    <div className="main-content">
      <Helmet>
        <title>HTTP</title>
        <meta
          name="description"
          content="In this article, we will take a look at the Hypertext Transfer Protocal, its methods and HTTP response status codes"
        />
      </Helmet>
      <main>
        <h1>Hypertext Transfer Protocal</h1>
        <p>
          Hypertext Transfer Protocol or HTTP is a communication protocal that
          allows the fetching of resources, such as HTML documents. It is the
          foundation of any data exchange on the Web and it is a client-server
          protocol, which means requests are initiated by the recipient, usually
          the Web browser.
        </p>
        <p>
          The messages sent by the client, usually a Web browser, are called
          requests and the messages sent by the server as an answer are called
          responses.
        </p>
        <h2>HTTP is stateless, but not sessionless</h2>
        <p>
          HTTP is stateless: there is no link between two requests being
          successively carried out on the same connection. This immediately has
          the prospect of being problematic for users attempting to interact
          with certain pages coherently, for example, using e-commerce shopping
          baskets. But while the core of HTTP itself is stateless, HTTP cookies
          allow the use of stateful sessions. Using header extensibility, HTTP
          Cookies are added to the workflow, allowing session creation on each
          HTTP request to share the same context, or the same state.
        </p>
        <h2>HTTP Flow</h2>
        <p>
          When a client wants to communicate with a server, it performs the
          following steps:
        </p>
        <ul>
          <li>
            <b>Open a TCP connection:</b> The TCP connection is used to send a
            request and receive an answer. The client may open a new connection,
            reuse an existing connection, or open several TCP connections to the
            servers.
          </li>
          <li>
            <b>Send an HTTP message:</b> HTTP messages (before HTTP/2) are
            human-readable. With HTTP/2, these simple messages are encapsulated
            in frames, making them impossible to read directly, but the
            principle remains the same. For example:
            <div className="note">
              <p>
                GET / HTTP/1.1 <br /> Host: en.wikipedia.org <br />
                Accept-Language: en
              </p>
            </div>
          </li>
          <li>
            Read the response sent by the server, such as:
            <div className="note">
              <p>
                HTTP/1.1 200 OK <br />
                Date: Sat, 09 Oct 2020 14:28:02 GMT <br />
                Server: Apache <br />
                Last-Modified: Tue, 01 Dec 2019 20:18:22 GMT <br />
                Accept-Ranges: bytes <br />
                Content-Length: 29769 <br />
                Content-Type: text/html <br /> <br />
                &lt;!DOCTYPE html... (here comes the 29769 bytes of the
                requested web page)
              </p>
            </div>
          </li>
          <li>Close or reuse the connection for further requests.</li>
        </ul>
        <h2>HTTP Request Methods</h2>
        <p>
          HTTP defines a set of request methods to indicate the desired action
          to be performed for a given resource. Although they can also be nouns,
          these request methods are sometimes referred to as HTTP verbs.
        </p>
        <ul>
          <li>
            <b>GET</b> The GET method requests a representation of the specified
            resource. Requests using GET should only retrieve data.
          </li>
          <li>
            <b>POST</b> The POST method is used to submit an entity to the
            specified resource, often causing a change in state or side effects
            on the server. The type of the body of the request is indicated by
            the Content-Type header.
          </li>
          <li>
            <b>PUT</b> The PUT method replaces all current representations of
            the target resource with the request payload.
          </li>
          <li>
            <b>DELETE</b> The DELETE method deletes the specified resource.
          </li>
          <li>
            <b>PATCH</b> The PATCH method is used to apply partial modifications
            to a resource. It is somewhat analogous to the "update" concept
            found in CRUD (in general, HTTP is different than CRUD, and the two
            should not be confused).
          </li>
          <li>
            <b>HEAD </b>The HEAD method asks for a response identical to that of
            a GET request, but without the response body. For example, if a URL
            might produce a large download, a HEAD request could read its
            Content-Length header to check the filesize without actually
            downloading the file.
          </li>
          <li>
            <b>CONNECT </b>The CONNECT method starts two-way communications with
            the requested resource. It can be used to open a tunnel.
          </li>
          <li>
            <b>OPTIONS</b> The OPTIONS method requests permitted communication
            options for a given URL or server. A client can specify a URL with
            this method, or an asterisk (*) to refer to the entire server.
          </li>
          <li>
            <b>TRACE</b> The TRACE method performs a message loop-back test
            along the path to the target resource, providing a useful debugging
            mechanism.
          </li>
        </ul>
        <h3>Properties of Request Methods</h3>
        <ul>
          <li>
            <b>Safe:</b> An HTTP method is safe if it doesn't alter the state of
            the server. In other words, a method is safe if it leads to a
            read-only operation. Several common HTTP methods are safe: GET,
            HEAD, or OPTIONS. All safe methods are also idempotent, but not all
            idempotent methods are safe. For example, PUT and DELETE are both
            idempotent but unsafe.
          </li>
          <li>
            <b>Idempotent:</b> An HTTP method is idempotent if an identical
            request can be made once or several times in a row with the same
            effect while leaving the server in the same state. In other words,
            an idempotent method should not have any side-effects (except for
            keeping statistics). Implemented correctly, the GET, HEAD, PUT, and
            DELETE methods are idempotent, but not the POST method. All safe
            methods are also idempotent.
          </li>
          <li>
            <b>Cacheable:</b> A cacheable response is an HTTP response that can
            be cached, that is stored to be retrieved and used later, saving a
            new request to the server.
          </li>
        </ul>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Method</th>
                <th>Request had Body</th>
                <th>Successful Response has body</th>
                <th>Safe</th>
                <th>Idempotent</th>
                <th>Cacheable</th>
                <th>Allowed in HTML Forms</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GET</td>
                <td>No </td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>POST</td>
                <td>Yes </td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
                <td>No</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>PUT</td>
                <td>Yes </td>
                <td>No</td>
                <td>No</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>DELETE</td>
                <td>May</td>
                <td>May</td>
                <td>No</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>PATCH</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>HEAD</td>
                <td>No</td>
                <td>No</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
              </tr>
              <tr>
                <td>CONNECT</td>
                <td>No</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
                <td>No</td>
                <td>No</td>
              </tr>

              <tr>
                <td>OPTIONS</td>
                <td>No</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
              <tr>
                <td>TRACE</td>
                <td>No</td>
                <td>No</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>No</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h2>HTTP Response status Codes</h2>
        <p>
          Status codes are issued by a server in response to a client's request
          made to the server. All HTTP response status codes are separated into
          five classes or categories. The first digit of the status code defines
          the class of response, while the last two digits do not have any
          classifying or categorization role. There are five classes defined by
          the standard:
        </p>
        <ul>
          <li>
            1XX informational response – the request was received, continuing
            process
          </li>
          <li>
            2XX successful – the request was successfully received, understood,
            and accepted
          </li>
          <li>
            3XX redirection – further action needs to be taken in order to
            complete the request
          </li>
          <li>
            4XX client error – the request contains bad syntax or cannot be
            fulfilled
          </li>
          <li>
            5XX server error – the server failed to fulfil an apparently valid
            request
          </li>
        </ul>
        <p>
          List of important Status codes are given below. Find all status codes
          at{" "}
          <a href="https://en.wikipedia.org/wiki/List_of_HTTP_status_codes">
            List of Status Codes.
          </a>
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Status Code</th>
                <th>Meaning</th>
                <th>Elaboration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>100</td>
                <td>Continue</td>
                <td>
                  Indicates that everything so far is OK and that the client
                  should continue with the request or ignore it if it is already
                  finished.
                </td>
              </tr>

              <tr>
                <td>200</td>
                <td>Ok</td>
                <td>Indicates that the request has succeeded.</td>
              </tr>
              <tr>
                <td>201</td>
                <td>Created</td>
                <td>
                  Indicates that the request has succeeded and has led to the
                  creation of a resource.
                </td>
              </tr>

              <tr>
                <td>400</td>
                <td>Bad Request</td>
                <td>
                  Indicates that the server cannot or will not process the
                  request due to something that is perceived to be a client
                  error (e.g., malformed request syntax, invalid request message
                  framing, or deceptive request routing).
                </td>
              </tr>
              <tr>
                <td>401</td>
                <td>Unauthorized</td>
                <td>
                  Indicates that the request has not been applied because it
                  lacks valid authentication credentials for the target
                  resource.
                </td>
              </tr>
              <tr>
                <td>403</td>
                <td>Forbidden</td>
                <td>
                  Indicates that the server understood the request but refuses
                  to authorize it.
                </td>
              </tr>
              <tr>
                <td>404</td>
                <td>Not Found</td>
                <td>
                  Indicates that the server can't find the requested resource.
                  Links that lead to a 404 page are often called broken or dead
                  links.
                </td>
              </tr>
              <tr>
                <td>500</td>
                <td>Internal Server Error</td>
                <td>
                  Indicates that the server encountered an unexpected condition
                  that prevented it from fulfilling the request.
                </td>
              </tr>
              <tr>
                <td>502</td>
                <td>Bad Gateway</td>
                <td>
                  Indicates that the server, while acting as a gateway or proxy,
                  received an invalid response from the upstream server.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <h2>HTTPS</h2>
        <p>
          HTTPS or Hypertext Transfer Protocol Secure is a secure version of the
          HTTP protocol. This protocol allows transferring the data in an
          encrypted form. The use of HTTPS protocol is mainly required where we
          need to transfer sensitive data such as the bank account details.
        </p>
        <p>
          HTTP is not encrypted and thus is vulnerable to man-in-the-middle and
          eavesdropping attacks, which can let attackers gain access to website
          accounts and sensitive information, and modify webpages to inject
          malware or advertisements. HTTPS is designed to withstand such attacks
          and is considered secure against them (with the exception of HTTPS
          implementations that use deprecated versions of SSL).
        </p>
        <p>
          To provide encryption, HTTPS uses an encryption protocol known as
          Transport Layer Security or, formerly, Secure Sockets Layer (SSL).
          HTTPS URLs begin with "https://" and use port 443 by default, whereas,
          HTTP URLs begin with "http://" and use port 80 by default.
        </p>
      </main>
    </div>
  );
}

export default AboutHTTP;
